import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import Colors from "../../styles/Colors";
import NavigationService from "../../services/NavigationService";
import LogoSource from "../../assets/images/Logo.png";
import {GAEvent} from "../../services/AnalyticsService";

interface Route {
    path: string | Array<string>,
    label: string,
    route?: Array<Route>,
}

interface Props {
    routes: Array<Route>,
}

const Header = ({routes}: Props) => {
    const buttonListRef = useRef<HTMLDivElement>(null)
    const expandButtonRef = useRef<HTMLElement>(null)

    const [activeRouteId, setActiveRouteId] = useState(0)
    const [isPhone, setIsPhone] = useState(false)

    const setDefaultActiveRoute = () => {
        const routePath = window.location.pathname

        for (let i = 0; i < routes.length; i++) {
            const currentRoute = routes[i]

            if (typeof currentRoute.path === "string" && currentRoute.path === routePath) {
                setActiveRouteId(i)
                return
            }

            if (typeof currentRoute.path === "object") {
                for (let j = 0; j < currentRoute.path.length; j++) {
                    const path = currentRoute.path[j]

                    if (path === routePath) {
                        setActiveRouteId(i)
                        return
                    }
                }
            }
        }
    }

    const handleLocationChange = () => {
        setDefaultActiveRoute()
    }

    const navigate = (routePath: string, routeName: string, routeId: number) => {
        const navDone = NavigationService.navigate(routePath, routeName)

        // if route change update active route
        if (navDone) {
            window.dispatchEvent(new Event("location change"))
            setActiveRouteId(routeId)
        }
    }

    const handleResize = () => {
        const {innerWidth} = window
        const isSmallSize = 768

        if (isPhone && innerWidth > isSmallSize) {
            setIsPhone(false)
            closePhoneHeader()
        }

        if (!isPhone && innerWidth <= isSmallSize) {
            setIsPhone(true)
        }
    }

    const handleWindowClick = (e: Event) => {
        // @ts-ignore
        const {headbtn} = e.target.dataset

        if (!headbtn && isPhone) {
            closePhoneHeader()
        }
    }

    const getRouteLink = () => {
        return routes.map((route, index) => {
            let path = route.path

            if (typeof route.path === "object") {
                path = route.path[0]
            }

            const isActive = activeRouteId === index

            const onClick = () => {
                GAEvent("Navigation", `${route.label} Button click`, "Button")
                navigate(path.toString(), route.label, index)
            }

            return (
                <RouteLink
                    onClick={onClick}
                    key={index}
                    isActive={isActive}
                    isPhone={isPhone}
                    data-headbtn={"btn"}
                >
                    {route.label}
                </RouteLink>
            )
        })
    }

    const handleExpandClick = () => {
        const buttonList = buttonListRef.current
        const expandButton = expandButtonRef.current

        if (buttonList && expandButton) {
            buttonList.classList.toggle("is-open")
            expandButton.classList.toggle("is-open")
        }
    }

    const closePhoneHeader = () => {
        const buttonList = buttonListRef.current
        const expandButton = expandButtonRef.current

        if (buttonList && expandButton) {
            buttonList.classList.remove("is-open")
            expandButton.classList.remove("is-open")
        }
    }

    const renderLogo = () => {
        return <Logo src={LogoSource} alt={"Logo EcoPatu'rhin"}/>
    }

    useEffect(() => {
        setDefaultActiveRoute()
        handleResize()

        window.addEventListener("resize", handleResize)
        window.addEventListener("click", handleWindowClick)
        window.addEventListener("location change", handleLocationChange)
        window.addEventListener("popstate", handleLocationChange)

        return () => {
            window.removeEventListener("resize", handleResize)
            window.removeEventListener("click", handleWindowClick)
            window.removeEventListener("location change", handleLocationChange)
            window.removeEventListener("popstate", handleLocationChange)
        }
    })

    return (
        <Wrapper>
            <HeaderContainer className={"container-fluid"}>
                {!isPhone && renderLogo()}

                <Container
                    className={"container-xl"}
                    isPhone={isPhone}
                >
                    {
                        isPhone && <PhoneExpandButton
                            className="fas fa-bars"
                            ref={expandButtonRef}
                            onClick={handleExpandClick}
                            data-headbtn={"btn"}
                        />
                    }

                    {isPhone && renderLogo()}

                    {isPhone && <RightDiv />}

                    {!isPhone && getRouteLink()}
                </Container>
            </HeaderContainer>

            {
                isPhone && <PhoneButtonContainer
                    data-headbtn={"btn"}
                    ref={buttonListRef}
                >
                    {getRouteLink()}
                </PhoneButtonContainer>
            }
        </Wrapper>
    )
}

export default Header

interface ContainerProps {
    isPhone?: boolean,
}

const Wrapper = styled.div<ContainerProps>`
  width: 100%;
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  margin-bottom: 20px;
  background-color: ${Colors.white};
  z-index: 9999;
`

const HeaderContainer = styled.div<ContainerProps>`
  height: 60px;
  box-shadow: ${Colors.headerShadow};
  position: relative;
  display: flex;
  flex-direction: row;
`

const Container = styled.div<ContainerProps>`
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.isPhone ? "space-between" : "center"};
  align-items: center;
`

const RightDiv = styled.div`
  width: 25px;
`

const PhoneExpandButton = styled.i<ContainerProps>`
  font-size: 1.5em;
  color: ${Colors.darkGrey};
  
  transition: ease;
  transition-duration: 200ms;
  
  &.is-open {
    color: ${Colors.green};
  }
`

const RouteLink = styled.button<RouteContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${props => props.isPhone ? "40px" : "100%"};
  width: ${props => props.isPhone ? "100%" : "auto"};
  padding: 0 25px;
  
  text-wrap: none;
  text-overflow: ellipsis;
  
  background-color: transparent;
  
  color: ${
    props => !props.isActive && props.isPhone
        ? Colors.darkGrey
        : Colors.green
};
  
  border: none;
  border-bottom: ${
    props => props.isActive && !props.isPhone
        ? `2px solid ${Colors.green}`
        : props.isPhone
            ? `1px solid ${Colors.veryLightGrey}`
            : "2px solid transparent"
};
  
  transition-duration: 200ms;
  
  &:hover {
    transition-duration: 200ms;
    cursor: ${props => props.isActive ? "default" : "pointer"};
    color: ${props => props.isActive ? Colors.green : Colors.lightGreen};
    border-bottom-color: ${props =>
    props.isActive && !props.isPhone
        ? Colors.green
        : props.isPhone
        ? Colors.veryLightGrey
        : Colors.lightGreen
};
  }
  
  &:focus {
    outline: none;
  }
`

interface RouteContainerProps {
    isActive: boolean,
    isPhone: boolean,
}

const PhoneButtonContainer = styled.div`
  position: absolute;
  width: 100%;
  background-color: ${Colors.white};
  box-shadow: ${Colors.headerDropdownShadow};
  z-index: 9999;
  
  top: 60px;
  left: 0;
  
  transform: scaleY(0);
  transform-origin: 50% 0;
  animation: linear;
  transition-duration: 200ms;
  
  &.is-open {
    transform: scaleY(1);
  }
`

const Logo = styled.img`
  height: 50px;
  
  @media (min-width: 769px) and (max-width: 945px){
    display: none;
  }
   
  @media (min-width: 769px) {
    top: 5px;
    position: absolute;
  }
`

